import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ProfessorList.css";
import { Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import axios from "axios";
import Modal from "../Modal/Modal";

const API_URL = process.env.REACT_APP_API_URL;
const CACHE_TTL = 15 * 60 * 1000;

function getCachedData(key) {
	const raw = localStorage.getItem(key);
	if (!raw) return null;
	const { data, timestamp } = JSON.parse(raw);
	return Date.now() - timestamp < CACHE_TTL ? data : null;
}

function setCachedData(key, data) {
	localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
}

function AnimateWhenVisible({
	children,
	animation,
	delay = 0,
	className = "",
}) {
	const [isVisible, setIsVisible] = useState(false);
	const ref = useRef(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 }
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => {
			if (ref.current) {
				observer.disconnect();
			}
		};
	}, []);

	const animations = {
		fadeIn: {
			hidden: { opacity: 0 },
			visible: { opacity: 1, transition: { duration: 0.6, delay } },
		},
		slideUp: {
			hidden: { opacity: 0, y: 50 },
			visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
		},
		slideRight: {
			hidden: { opacity: 0, x: -50 },
			visible: { opacity: 1, x: 0, transition: { duration: 0.6, delay } },
		},
		slideLeft: {
			hidden: { opacity: 0, x: 50 },
			visible: { opacity: 1, x: 0, transition: { duration: 0.6, delay } },
		},
		scale: {
			hidden: { opacity: 0, scale: 0.8 },
			visible: {
				opacity: 1,
				scale: 1,
				transition: { duration: 0.6, delay },
			},
		},
	};

	const selectedAnimation = animations[animation] || animations.fadeIn;

	return (
		<motion.div
			ref={ref}
			initial="hidden"
			animate={isVisible ? "visible" : "hidden"}
			variants={selectedAnimation}
			className={className}
		>
			{children}
		</motion.div>
	);
}

export default function ProfessorsList() {
	const [professors, setProfessors] = useState([]);
	const [openSelect, setOpenSelect] = useState({
		department: false,
		rank: false,
		expertise: false,
	});
	const tableControls = useAnimation();
	const tableRef = useRef(null);
	const navigate = useNavigate();
	const [searchData, setSearchData] = useState({
		persian_lname: localStorage.getItem("p-lastname") || "",
		faculty: localStorage.getItem("p-faculty") || "",
		scientific_rank: localStorage.getItem("p-rank") || "",
	});
	const [modalData, setModalData] = useState({
		isVisible: false,
		message: "",
		type: "",
	});
	const [isMounted, setIsMounted] = useState(false);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

	const handleSort = (key) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
		}));
	};

	const scientificRank = [
		"همه رتبه ها",
		"مربی",
		"استادیار",
		"دانشیار",
		"استاد",
	];

	const faculty = [
		"همه دانشکده ها",
		"مهندسی کامپیوتر",
		"مهندسی برق",
		"مهندسی مکانیک",
		"مهندسی عمران",
		"مهندسی هوافضا",
		"مهندسی صنایع",
		"مهندسي و علم مواد",
		"مهندسی نقشه برداری",
		"ریاضی",
		"فیزیک",
		"شیمی",
		"مركز آموزش هاي عمومی",
	];

	const arabicToPersianMap = {
		ك: "ک",
		ي: "ی",
		ة: "ه",
		أ: "ا",
		إ: "ا",
		آ: "آ",
		ؤ: "و",
		ئ: "ی",
		ٱ: "ا",
		"ٰ": "‌",
		"": "",
	};

	const convertArabicToPersian = (text) => {
		if (!text) return text;

		return text
			.split("")
			.map((char) => arabicToPersianMap[char] || char)
			.join("")
			.replace(/\u0643/g, "\u06A9")
			.replace(/\u064A/g, "\u06CC")
			.replace(/\u0626/g, "\u06CC")
			.replace(/\u0624/g, "\u0648")
			.replace(/\u0625/g, "\u0627")
			.replace(/\u0622/g, "\u0622")
			.replace(/\u0623/g, "\u0627");
	};

	useEffect(() => {
		setIsMounted(true);
		return () => setIsMounted(false);
	}, []);

	useEffect(() => {
		const handleBeforeUnload = () => {
			localStorage.removeItem("p-lastname");
			localStorage.removeItem("p-faculty");
			localStorage.removeItem("p-rank");
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		localStorage.setItem("p-lastname", searchData.persian_lname);
		localStorage.setItem("p-faculty", searchData.faculty);
		localStorage.setItem("p-rank", searchData.scientific_rank);
	}, [searchData]);

	useEffect(() => {
		const filterData = async () => {
			const cacheKey = `prof_cache_${searchData.persian_lname}_${searchData.faculty}_${searchData.scientific_rank}`;
			
			let cached = null;
			try {
				cached = getCachedData(cacheKey);
			} catch (e) {
				console.warn('Cache read failed, fetching fresh:', e);
			}
			if (cached) {
				setProfessors(cached);
				return;
			}
			
			try {
				const response = await axios.get(
					`${API_URL}/public/list?persian_lname=${searchData.persian_lname}&faculty=${searchData.faculty}&scientific_rank=${searchData.scientific_rank}`,
					{
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					}
				);

				const processedData = response.data.map((professor) => ({
					...professor,
					persian_fname: convertArabicToPersian(
						professor.persian_fname
					),
					persian_lname: convertArabicToPersian(
						professor.persian_lname
					),
					faculty: convertArabicToPersian(professor.faculty),
					scientific_rank: convertArabicToPersian(
						professor.scientific_rank
					),
				}));

				try {
					setCachedData(cacheKey, processedData);
				} catch (e) {
					console.warn('Cache write failed:', e);
				}
				
				setProfessors(processedData);
			} catch (error) {
				setModalData({
					isVisible: true,
					message: "مشکلی پیش آمده است. لطفا دوباره تلاش کنید",
					type: "error",
				});
			}
		};
		filterData();
	}, [searchData]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && isMounted) {
					tableControls.start("visible");
					observer.disconnect();
				}
			},
			{ threshold: 0.1 }
		);
		if (tableRef.current) {
			observer.observe(tableRef.current);
		}
		return () => {
			if (tableRef.current) {
				observer.disconnect();
			}
		};
	}, [tableControls]);

	const toggleSelect = (name) => {
		setOpenSelect((prev) => ({
			...prev,
			[name]: !prev[name],
		}));
	};

	const closeAllSelects = () => {
		setOpenSelect({
			department: false,
			rank: false,
			expertise: false,
		});
	};

	const handleChange = (event) => {
		setSearchData({
			...searchData,
			[event.target.name]: event.target.value,
		});
	};

	return (
		<div className="app-container">
			<main className="main-content">
				<div className="main-container">
					<div className="content-section">
						<AnimateWhenVisible animation="scale" delay={0.2}>
							<div className="filter-container-home">
								<div className="filter-content">
									<div className="filter-grid">
										<AnimateWhenVisible
											animation="slideRight"
											delay={0.3}
										>
											<div className="filter-item">
												<label
													htmlFor="name"
													className="filter-label"
												>
													نام و نام خانوادگی
												</label>
												<div className="input-wrapper">
													<Search className="search-icon" />
													<input
														id="name"
														name="persian_lname"
														type="search"
														className="search-input medium-text"
														value={
															searchData.persian_lname
														}
														onChange={handleChange}
														style={{
															fontFamily:
																"Vazir, sans-serif",
														}}
													/>
												</div>
											</div>
										</AnimateWhenVisible>
										<AnimateWhenVisible
											animation="slideRight"
											delay={0.4}
										>
											<div className="filter-item">
												<label
													htmlFor="department"
													className="filter-label"
												>
													دانشکده/گروه
												</label>
												<div className="input-wrapper">
													<button
														id="department"
														onClick={() =>
															toggleSelect(
																"department"
															)
														}
														className="select-button light-text"
													>
														<span className="select-text">
															{searchData.faculty ||
																"همه دانشکده ها"}
														</span>
														<ChevronDown className="select-icon" />
													</button>
													{openSelect.department && (
														<div className="dropdown-menu">
															{faculty.map(
																(fac) => (
																	<div
																		key={
																			fac
																		}
																		className="dropdown-item"
																		onClick={() => {
																			setSearchData(
																				{
																					...searchData,
																					faculty:
																						fac ===
																						"همه دانشکده ها"
																							? ""
																							: fac,
																				}
																			);
																			closeAllSelects();
																		}}
																	>
																		{fac}
																	</div>
																)
															)}
														</div>
													)}
												</div>
											</div>
										</AnimateWhenVisible>

										<AnimateWhenVisible
											animation="slideRight"
											delay={0.5}
										>
											<div className="filter-item">
												<label
													htmlFor="rank"
													className="filter-label"
												>
													رتبه
												</label>
												<div className="input-wrapper">
													<button
														id="rank"
														onClick={() =>
															toggleSelect("rank")
														}
														className="select-button light-text"
													>
														<span className="select-text">
															{searchData.scientific_rank ||
																"همه رتبه ها"}
														</span>
														<ChevronDown className="select-icon" />
													</button>
													{openSelect.rank && (
														<div className="dropdown-menu">
															{scientificRank.map(
																(rank) => (
																	<div
																		key={
																			rank
																		}
																		className="dropdown-item"
																		onClick={() => {
																			setSearchData(
																				{
																					...searchData,
																					scientific_rank:
																						rank ===
																						"همه رتبه ها"
																							? ""
																							: rank,
																				}
																			);
																			closeAllSelects();
																		}}
																	>
																		{rank}
																	</div>
																)
															)}
														</div>
													)}
												</div>
											</div>
										</AnimateWhenVisible>
									</div>
								</div>
							</div>
						</AnimateWhenVisible>
						<div className="table-container-home">
								<div ref={tableRef} className="table-scroll">
									<table className="professors-table-home">
										<thead className="table-header">
											<tr>
												<th className="table-cell name-cell" onClick={() => handleSort("persian_lname")}>
													<div className="header-with-icon">
														نام
														{sortConfig.key === "persian_lname"
															? sortConfig.direction === "asc"
																? <ArrowUp className="sort-icon active" />
																: <ArrowDown className="sort-icon active" />
															: <ArrowUpDown className="sort-icon" />}
													</div>
												</th>
												<th className="table-cell department-cell" onClick={() => handleSort("faculty")}>
													<div className="header-with-icon">
														دانشکده
														{sortConfig.key === "faculty"
															? sortConfig.direction === "asc"
																? <ArrowUp className="sort-icon active" />
																: <ArrowDown className="sort-icon active" />
															: <ArrowUpDown className="sort-icon" />}
													</div>
												</th>
												<th className="table-cell rank-cell" onClick={() => handleSort("scientific_rank")}>
													<div className="header-with-icon">
														رتبه
														{sortConfig.key === "scientific_rank"
															? sortConfig.direction === "asc"
																? <ArrowUp className="sort-icon active" />
																: <ArrowDown className="sort-icon active" />
															: <ArrowUpDown className="sort-icon" />}
													</div>
												</th>
												<th className="table-cell profile-cell">
													پروفایل
												</th>
											</tr>
										</thead>
									<motion.tbody>
										{professors.length > 0 ? (
											[...professors].sort((a, b) => {
												if (!sortConfig.key) return 0;
												const valA = a[sortConfig.key] || "";
												const valB = b[sortConfig.key] || "";
												return sortConfig.direction === "asc"
													? valA.localeCompare(valB, "fa")
													: valB.localeCompare(valA, "fa");
											}).map((professor) => (
													<motion.tr
														key={professor.id}
														initial={{ opacity: 0 }}
														animate={{ opacity: 1 }}
														transition={{
															duration: 0.5,
														}}
														className="table-row"
													>
														<td className="table-cell name-data">
															{
																professor.persian_fname
															}{" "}
															{
																professor.persian_lname
															}
														</td>
														<td className="table-cell department-data">
															{professor.faculty}
														</td>
														<td className="table-cell rank-data">
															{
																professor.scientific_rank
															}
														</td>
														<td className="table-cell profile-data">
															<button
																className="view-button medium-text"
																onClick={() =>
																	navigate(
																		`/home/professor-profile/${professor.user_id}`
																	)
																}
															>
																مشاهده
															</button>
														</td>
													</motion.tr>
												))
											) : (
												<tr>
													<td
														colSpan="4"
														className="no-data"
													>
														هیچ استادی یافت نشد
													</td>
												</tr>
											)}
										</motion.tbody>
									</table>
								</div>
							</div>
					</div>
				</div>
			</main>
			<Modal
				message={modalData.message}
				type={modalData.type}
				isVisible={modalData.isVisible}
				onClose={() => setModalData({ ...modalData, isVisible: false })}
			/>
		</div>
	);
}
